import type { RegisteredParticipant } from '@/state/stores/registrationStore';
import type {
  EnumAzDanceBookingParticipantUpsertDtoGender,
  EnumAzDanceBookingParticipantUpsertDtoLevel,
  EnumAzDanceBookingParticipantUpsertDtoRole,
} from '@/api/api-client/service';
import {
  EnumAzDanceBookingParticipantUpsertDtoGender as GenderEnum,
  EnumAzDanceBookingParticipantUpsertDtoLevel as LevelEnum,
  EnumAzDanceBookingParticipantUpsertDtoRole as RoleEnum,
} from '@/api/api-client/service';

export function mapRoleToApi(
  role: RegisteredParticipant['role'],
): EnumAzDanceBookingParticipantUpsertDtoRole | undefined {
  if (role === 'leader') return RoleEnum.leader;
  if (role === 'follower') return RoleEnum.follower;
  if (role === 'switch') return RoleEnum.switch;
  return undefined;
}

export function mapLevelToApi(
  level: RegisteredParticipant['level'],
): EnumAzDanceBookingParticipantUpsertDtoLevel | undefined {
  if (level === 'beginner') return LevelEnum.beginner;
  if (level === 'intermediate') return LevelEnum.intermediate;
  if (level === 'advanced') return LevelEnum.advanced;
  if (level === 'professional') return LevelEnum.professional;
  return undefined;
}

export function parseOptionalAge(
  raw: string | number | null | undefined,
): number | undefined {
  if (raw === '' || raw == null) return undefined;
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return undefined;
    const n = Math.trunc(raw);
    return n >= 0 && n <= 150 ? n : undefined;
  }
  const s = String(raw).trim();
  if (!s) return undefined;
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) && n >= 0 && n <= 150 ? n : undefined;
}

export function mapGenderToApi(
  g: RegisteredParticipant['gender'],
): EnumAzDanceBookingParticipantUpsertDtoGender | undefined {
  if (g === 'mr') return GenderEnum.male;
  if (g === 'ms') return GenderEnum.female;
  if (g === GenderEnum.male) return GenderEnum.male;
  if (g === GenderEnum.female) return GenderEnum.female;
  if (g === GenderEnum.other) return GenderEnum.other;
  if (g === GenderEnum.prefer_not_to_say) return GenderEnum.prefer_not_to_say;
  return undefined;
}

/** Backend AzDanceParticipantContactType */
export function mapContactTypeToApi(
  ct: RegisteredParticipant['contactType'],
  instagramFallback?: string,
): 'instagram' | 'telegram' | 'whatsapp' {
  if (ct === 'whatsapp') return 'whatsapp';
  if (ct === 'instagram') return 'instagram';
  if (ct === 'telegram') return 'telegram';
  if (instagramFallback?.trim()) return 'instagram';
  return 'telegram';
}

export function mapContactValueForApi(p: RegisteredParticipant): string | undefined {
  const v = (p.contactValue ?? '').trim();
  if (v) return v;
  const ig = (p.instagram ?? '').trim();
  if (ig) return ig;
  return undefined;
}

/** `country` from UI → ISO-like `countryCode` when plausible. */
export function mapCountryToCode(country?: string): string | undefined {
  const c = country?.trim();
  if (c && /^[A-Za-z]{2}$/.test(c)) return c.toUpperCase();
  return undefined;
}

export type AzDanceRegistrationModeApi = 'default' | 'championship';

/** Тело запросов к API: явный режим; при отсутствии в сторе — из флага чемпионата. */
export function registrationModeForApi(
  p: Pick<RegisteredParticipant, 'registrationMode' | 'isChampionshipParticipant'>,
): AzDanceRegistrationModeApi {
  if (p.registrationMode === 'championship' || p.registrationMode === 'default') {
    return p.registrationMode;
  }
  return p.isChampionshipParticipant ? 'championship' : 'default';
}

/**
 * `lookingForPartner` for booking APIs: only **festival** (`default`) participants use it
 * (e.g. lessons in a pair). Championship rows must send `null` so the server does not keep a stale flag.
 */
export function lookingForPartnerForBookingApi(
  p: Pick<RegisteredParticipant, 'isChampionshipParticipant' | 'lookingForPartner'>,
): boolean | null {
  if (p.isChampionshipParticipant === true) {
    return null;
  }
  if (p.lookingForPartner === true) return true;
  if (p.lookingForPartner === false) return false;
  return null;
}
